import type { AgreementId, AgreementType, ConsentId } from './Agreement.ts'
import type { DocumentWithAuthor, DocumentWithSoftDelete, DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { OrgId, RequestLimit, UserId } from './ids.ts'
import type { JobFeeType } from './Job.ts'
import type { OrgAllowedCreditUser } from './OrgTransaction.ts'
import type { RelatedItems } from './RelatedItems.ts'

export const ORG_GOV_TYPE = 'verejny-sektor'

export enum OrgType {
    publicSector = 'verejny-sektor',
    nonProfit = 'neziskova-organizacia',
    school = 'skola',
    privateSector = 'sukromny-sektor',
    startUp = 'start-up',
    other = 'ine'
}

export const OrgTypeForGTM = {
    [OrgType.publicSector]: 'public',
    [OrgType.nonProfit]: 'non_profit',
    [OrgType.school]: 'school',
    [OrgType.privateSector]: 'private',
    [OrgType.startUp]: 'start_up',
    [OrgType.other]: 'project'
}

export type MemberInvitation = {
    email: string
    note: string
    invitedById: UserId
    createdAt: Date
}

export interface OrgSocialNetworks {
    facebook?: string
    linkedin?: string
    instagram?: string
}

export enum OrgOpportunitiesType {
    ACTIVE = 'active',
    ALL = 'all'
}

export type OrgOpportunitiesCount = {
    [OrgOpportunitiesType.ACTIVE]: number
    [OrgOpportunitiesType.ALL]: number
}

export enum OrgOpportunities {
    JOB = 'jobs',
    PROJECT = 'projects',
    SKILL = 'skills'
}

type OrgOpportunitiesDataItems = {
    [OrgOpportunities.JOB]: OrgOpportunitiesCount
    [OrgOpportunities.SKILL]: OrgOpportunitiesCount
    [OrgOpportunities.PROJECT]: OrgOpportunitiesCount
}

export interface ConsentsData {
    [AgreementType.jobMediationContract]?: ConsentData
    [AgreementType.internshipMediationContract]?: ConsentData
    [AgreementType.privateDataPolicy]?: ConsentData
    [AgreementType.jobMediationAgreement]?: ConsentData
    [AgreementType.marketingConsent]?: ConsentData
    [AgreementType.audiovisualPhotoConsent]?: ConsentData
    [AgreementType.jobApplicationConsent]?: ConsentData
    [AgreementType.selfKnowledgeConsent]?: ConsentData
    [AgreementType.personalDataLeafCommunityConsent]?: ConsentData
    [AgreementType.personalDataLeafVisionConsent]?: ConsentData
    [AgreementType.newsletterConsent]?: ConsentData
    [AgreementType.cooperationAgreement]?: ConsentData
    [AgreementType.jobAdvertisingTermsAndConditions]?: ConsentData
}

export interface ConsentData {
    accepted: boolean
    agreementId?: AgreementId
    consentId?: ConsentId
    givenAt?: Timestamp
    revokedAt?: Timestamp
    expiredAt?: Timestamp
}

export enum OrgGovPaidProfileLevel {
    PLUS = 'plus',
    PREMIUM = 'premium',
    PARTNER = 'partner'
}

export type PaymentInfo = {
    isPaid: boolean
    lastPaymentAt: Timestamp
    expiresAt: Timestamp
}

export type OrgGovPaidProfilePlan = PaymentInfo & {
    level: OrgGovPaidProfileLevel
}

export interface HistoryRecord {
    requestedById: UserId
    requestedPlan: OrgGovPaidProfileLevel
    requestedAt: Timestamp
}

export interface PaidProfileInfo {
    plan: OrgGovPaidProfilePlan
    extras?: OrgPaidProfileExtras
    updatesHistory?: HistoryRecord[]
}

export type OrgPaidProfileExtras = Record<string, PaymentInfo>

export type Org = DocumentWithTimestamps &
    DocumentWithAuthor &
    DocumentWithSoftDelete & {
        id: OrgId
        memberIds: UserId[]
        memberInvitations?: MemberInvitation[]
        memberInvitationsEmails?: string[]
        title: string
        slug: string
        image: string
        galleryImages: string[]
        galleryVideos: string[]
        desc: string
        address: string
        zip: string
        geocity: string
        country: string
        ico: string
        dic: string
        icDph: string
        registerNumber: string
        personInCharge: string
        type: OrgType
        lastJobFeeType?: JobFeeType
        phone: string
        web: string
        social: OrgSocialNetworks
        interests: string[]
        relatedItems?: RelatedItems
        consents?: ConsentsData
        opportunities?: OrgOpportunitiesDataItems
        requestLimits?: {
            volunteering?: RequestLimit
        }
        paidProfileInfo?: PaidProfileInfo | null
        allowedCreditUsers?: OrgAllowedCreditUser[]
    }

export type OrgMeta = Pick<Org, 'id' | 'title' | 'image' | 'type' | 'slug'> & { key?: string }

export type OrgFormData = Partial<Org>

export type OrgIndexed = Org & OrgOpportunitiesData

export const OrgGovPaidProfilePrices = {
    [OrgGovPaidProfileLevel.PLUS]: '590',
    [OrgGovPaidProfileLevel.PREMIUM]: '5 990',
    [OrgGovPaidProfileLevel.PARTNER]: 'od 10 000'
} as const

export function defaultOrg(): OrgFormData {
    return {
        title: '',
        slug: '',
        address: '',
        zip: '',
        geocity: '',
        country: 'Slovensko',
        desc: '',
        ico: '',
        dic: '',
        icDph: '',
        web: '',
        phone: '',
        interests: [],
        social: { facebook: '', linkedin: '', instagram: '' },
        allowedCreditUsers: [],
        type: undefined
    }
}

export type OrgOpportunitiesData = {
    opportunities: OrgOpportunitiesDataItems
}

export type RecountOrgOpportunity = { id: string; data: OrgOpportunitiesData }

export type AffiliationLinkPayload = {
    orgId: OrgId
    createdBy: UserId
    redirect?: string
}

export type AffiliationLinkResponse = {
    orgId: OrgId
    redirect?: string
}
